import { WorkflowPhase, WorkspaceCode } from '@atlas/types'
import {
	ListSkeleton,
	SelectField,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@atlas/ui-components'
import { Badge, Button } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import type { WorkflowStatusDTO, WorkflowStatusUpdateBody } from '@/domain/workflow-admin.types'
import { TextField } from '@/pages/Classification/FormField'
import { EmptyState, ErrorBanner, ErrorState, FieldError } from './states'

/**
 * A workflow's statuses in configured position order, with inline editing of
 * the fields the administration contract exposes. Workspace assignment and
 * asset-type workspace overrides are deliberately absent - this screen edits
 * the status row itself and nothing else.
 *
 * Position is read-only here. It is server-owned and moves only through the
 * reorder operation, which the move controls call: a typed absolute value
 * shifts no sibling, and `workflow_status.position` is not unique, so the
 * obvious attempt at "put this third" used to leave two statuses tied.
 */

export interface StatusListProps {
	statuses: WorkflowStatusDTO[]
	isLoading?: boolean
	error?: unknown
	onRetry?: () => void
	onSave: (statusId: string, body: WorkflowStatusUpdateBody) => void
	/** Move one status one place in the active sequence. */
	onMove: (statusId: string, direction: 'up' | 'down') => void
	onDelete?: (statusId: string) => void
	/** True while a reorder is in flight - move controls disable. */
	isReordering?: boolean
	/** Id of the status whose mutation is in flight; its row disables submit. */
	pendingStatusId?: string | null
	/** Server validation message keyed by field, for the row being edited. */
	fieldErrors?: Partial<Record<keyof WorkflowStatusUpdateBody, string>>
	/** Server error not attributable to a single field. */
	saveError?: unknown
}

const PHASES = Object.values(WorkflowPhase).map((value) => ({ value, label: value }))
const WORKSPACES = Object.values(WorkspaceCode).map((value) => ({ value, label: value }))

interface Draft {
	code: string
	label: string
	phase: WorkflowPhase
	workspaceDefault: WorkspaceCode
	isInitial: boolean
	isTerminal: boolean
	isActive: boolean
}

const draftOf = (status: WorkflowStatusDTO): Draft => ({
	code: status.code,
	label: status.label,
	phase: status.phase,
	workspaceDefault: status.workspaceDefault,
	isInitial: status.isInitial,
	isTerminal: status.isTerminal,
	isActive: status.isActive,
})

const bodyOf = (draft: Draft): WorkflowStatusUpdateBody => ({
	code: draft.code.trim(),
	label: draft.label.trim(),
	phase: draft.phase,
	workspaceDefault: draft.workspaceDefault,
	isInitial: draft.isInitial,
	isTerminal: draft.isTerminal,
	isActive: draft.isActive,
})

function Flags({ status }: { status: WorkflowStatusDTO }) {
	return (
		<span className="flex flex-wrap gap-1">
			{status.isInitial && (
				<Badge variant="primary" size="sm">
					initial
				</Badge>
			)}
			{status.isTerminal && (
				<Badge variant="secondary" size="sm">
					terminal
				</Badge>
			)}
			{!status.isActive && (
				<Badge variant="muted" size="sm">
					inactive
				</Badge>
			)}
		</span>
	)
}

/** Move controls over the active sequence; an inactive status holds no place in it and offers none. */
function MoveControls({
	status,
	activeIndex,
	activeCount,
	onMove,
	isReordering,
}: {
	status: WorkflowStatusDTO
	activeIndex: number
	activeCount: number
	onMove: StatusListProps['onMove']
	isReordering?: boolean
}) {
	if (activeIndex === -1) return null
	return (
		<>
			<Button
				variant="outline"
				isDisabled={activeIndex === 0 || isReordering}
				onPress={() => onMove(status.statusId, 'up')}
				aria-label={`Move ${status.code} up`}
			>
				↑
			</Button>
			<Button
				variant="outline"
				isDisabled={activeIndex === activeCount - 1 || isReordering}
				onPress={() => onMove(status.statusId, 'down')}
				aria-label={`Move ${status.code} down`}
			>
				↓
			</Button>
		</>
	)
}

/** The persisted row: its values, its move controls, and the actions that leave it. */
function StatusReadRow({
	status,
	activeIndex,
	activeCount,
	onMove,
	onDelete,
	onEdit,
	isReordering,
}: {
	status: WorkflowStatusDTO
	activeIndex: number
	activeCount: number
	onMove: StatusListProps['onMove']
	onDelete?: StatusListProps['onDelete']
	onEdit: () => void
	isReordering?: boolean
}) {
	return (
		<TableRow data-testid={`status-row-${status.code}`}>
			<TableCell className="font-medium">{status.position}</TableCell>
			<TableCell>{status.code}</TableCell>
			<TableCell>{status.label}</TableCell>
			<TableCell>{status.phase}</TableCell>
			<TableCell>{status.workspaceDefault}</TableCell>
			<TableCell>
				<Flags status={status} />
			</TableCell>
			<TableCell className="flex gap-2">
				<MoveControls
					status={status}
					activeIndex={activeIndex}
					activeCount={activeCount}
					onMove={onMove}
					isReordering={isReordering}
				/>
				<Button variant="outline" onPress={onEdit} aria-label={`Edit ${status.code}`}>
					Edit
				</Button>
				{onDelete && (
					<Button variant="outline" onPress={() => onDelete(status.statusId)} aria-label={`Delete ${status.code}`}>
						Delete
					</Button>
				)}
			</TableCell>
		</TableRow>
	)
}

function StatusEditRow({
	status,
	activeIndex,
	activeCount,
	props,
}: {
	status: WorkflowStatusDTO
	activeIndex: number
	activeCount: number
	props: StatusListProps
}) {
	const { onSave, onMove, onDelete, pendingStatusId, fieldErrors, saveError, isReordering } = props
	const [draft, setDraft] = useState<Draft>(() => draftOf(status))
	const [editing, setEditing] = useState(false)
	const isPending = pendingStatusId === status.statusId
	const set =
		<K extends keyof Draft>(key: K) =>
		(value: Draft[K]) =>
			setDraft({ ...draft, [key]: value })

	// A refused code is restored to the persisted value as soon as the server
	// says so, so the screen never shows the rejected code as if it had been
	// accepted. The message stays visible next to the field.
	const rejectedCode = fieldErrors?.code
	useEffect(() => {
		if (rejectedCode) setDraft((current) => ({ ...current, code: status.code }))
	}, [rejectedCode, status.code])

	// Leaving edit mode restores the persisted row, so a value the server
	// refused is never left on screen as if it had been accepted.
	const cancel = () => {
		setDraft(draftOf(status))
		setEditing(false)
	}

	if (!editing) {
		return (
			<StatusReadRow
				status={status}
				activeIndex={activeIndex}
				activeCount={activeCount}
				onMove={onMove}
				onDelete={onDelete}
				onEdit={() => setEditing(true)}
				isReordering={isReordering}
			/>
		)
	}

	return (
		<TableRow data-testid={`status-edit-${status.code}`}>
			<TableCell colSpan={7}>
				<div className="flex flex-wrap items-end gap-3">
					<div className="flex flex-col">
						<TextField label="Code" value={draft.code} onChange={set('code')} />
						<FieldError message={fieldErrors?.code} />
					</div>
					<TextField label="Label" value={draft.label} onChange={set('label')} />
					<SelectField
						isClearable={false}
						label="Phase"
						value={draft.phase}
						onChange={(v) => set('phase')(v as WorkflowPhase)}
						options={PHASES}
					/>
					<SelectField
						isClearable={false}
						label="Workspace default"
						value={draft.workspaceDefault}
						onChange={(v) => set('workspaceDefault')(v as WorkspaceCode)}
						options={WORKSPACES}
					/>
					<label className="flex items-center gap-2 text-sm">
						<input type="checkbox" checked={draft.isInitial} onChange={(e) => set('isInitial')(e.target.checked)} />
						Initial
					</label>
					<label className="flex items-center gap-2 text-sm">
						<input type="checkbox" checked={draft.isTerminal} onChange={(e) => set('isTerminal')(e.target.checked)} />
						Terminal
					</label>
					<label className="flex items-center gap-2 text-sm">
						<input type="checkbox" checked={draft.isActive} onChange={(e) => set('isActive')(e.target.checked)} />
						Active
					</label>
					<Button
						isDisabled={isPending || !draft.code.trim() || !draft.label.trim()}
						onPress={() => onSave(status.statusId, bodyOf(draft))}
						aria-label={`Save ${status.code}`}
					>
						{isPending ? 'Saving…' : 'Save'}
					</Button>
					<Button variant="outline" onPress={cancel}>
						Cancel
					</Button>
				</div>
				{saveError && !fieldErrors?.code ? <ErrorBanner error={saveError} title="Could not save status" /> : null}
			</TableCell>
		</TableRow>
	)
}

export function StatusList(props: StatusListProps) {
	const { statuses, isLoading, error, onRetry } = props
	if (isLoading) return <ListSkeleton label="Loading statuses" />
	if (error) return <ErrorState error={error} onRetry={onRetry} title="Could not load statuses" />
	if (statuses.length === 0) {
		return (
			<EmptyState
				title="No statuses yet"
				description="This workflow has no statuses. Add one to give work somewhere to start."
			/>
		)
	}
	const ordered = [...statuses].sort((a, b) => a.position - b.position)
	// The sequence is defined over the active set only: an inactive status is
	// listed for administration but holds no place in it, so it carries no move
	// controls - the same rule the resolver-rule list follows.
	const activeOrder = ordered.filter((status) => status.isActive).map((status) => status.statusId)
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Position</TableHead>
					<TableHead>Code</TableHead>
					<TableHead>Label</TableHead>
					<TableHead>Phase</TableHead>
					<TableHead>Workspace</TableHead>
					<TableHead>Flags</TableHead>
					<TableHead>Actions</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{ordered.map((status) => (
					<StatusEditRow
						key={status.statusId}
						status={status}
						activeIndex={activeOrder.indexOf(status.statusId)}
						activeCount={activeOrder.length}
						props={props}
					/>
				))}
			</TableBody>
		</Table>
	)
}
